import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOMeta from '@/components/feature/SEOMeta';
import { faqs } from '@/mocks/articles';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9q89cee3oq4jqumtht0';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    formData.delete('phone_alt');

    const urlSearchParams = new URLSearchParams();
    formData.forEach((value, key) => {
      urlSearchParams.append(key, value as string);
    });

    fetch(FORM_SUBMIT_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: urlSearchParams.toString(),
    })
      .then(async (res) => {
        const text = await res.text();
        let parsed;
        try { parsed = JSON.parse(text); } catch { parsed = null; }
        return { ok: res.ok, parsed, text };
      })
      .then(({ ok, parsed, text }) => {
        if (ok && parsed?.code === 'OK') {
          setFormStatus('success');
          setFormError('');
          form.reset();
        } else {
          const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || text || '提交失败，请稍后再试';
          if (msg.toLowerCase().includes('spam') || msg.includes('form data is spam')) {
            setFormStatus('success');
            form.reset();
            return;
          }
          setFormStatus('error');
          setFormError(msg);
        }
      })
      .catch(() => {
        setFormStatus('error');
        setFormError('网络错误，请稍后再试');
      });
  };

  return (
    <>
      <SEOMeta
        title="关于我们 | 臻品阁 - 高品质成人精品品牌"
        description="臻品阁品牌介绍，专业甄选全球高品质成人精品。品质保障、私密配送、贴心服务。了解我们的品牌故事、品质承诺与联系方式。"
        keywords="成人精品品牌,臻品阁,成人精品推荐,高品质成人精品,成人精品购买"
        canonicalUrl="https://www.example.com/about"
      />

      {/* AboutPage Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于臻品阁',
            description: '臻品阁 - 高品质成人精品精选平台，安全私密、品质保障',
            url: 'https://www.example.com/about',
            isPartOf: {
              '@type': 'WebSite',
              name: '臻品阁',
              url: 'https://www.example.com',
            },
          }),
        }}
      />

      {/* BreadcrumbList Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.example.com/' },
              { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://www.example.com/about' },
            ],
          }),
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 md:pt-24">
          {/* Page Header */}
          <section className="w-full py-10 md:py-14 px-6 md:px-10 bg-background-100/50">
            <div className="max-w-7xl mx-auto">
              <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-4" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
                <i className="ri-arrow-right-s-line" />
                <span className="text-foreground-900 font-medium">关于我们</span>
              </nav>
              <h1 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900">
                关于臻品阁
              </h1>
              <p className="mt-2 text-sm text-foreground-600">
                专业甄选，品质生活
              </p>
            </div>
          </section>

          {/* Brand Story */}
          <section className="w-full py-14 md:py-20 px-6 md:px-10 bg-background-50">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
                <div>
                  <p className="text-xs md:text-sm font-label text-secondary-500 tracking-[0.2em] uppercase mb-2">
                    Our Story
                  </p>
                  <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-4">
                    品牌故事
                  </h2>
                  <div className="space-y-3 text-sm md:text-base text-foreground-700 leading-relaxed">
                    <p>
                      臻品阁创立于2020年，由一群对生活品质有着极致追求的专业人士共同发起。我们深知，在成人精品领域，品质与安全是消费者最核心的关切。
                    </p>
                    <p>
                      经过多年的行业深耕与全球供应链整合，臻品阁已建立起一套严格的选品标准体系。每一款上架产品都经过我们专业团队的实测验证，确保在材质安全、功能设计、用户体验等方面达到优秀水准。
                    </p>
                    <p>
                      我们致力于打破传统成人精品市场的刻板印象，以高端化、品质化、美学化的产品理念，为追求品质生活的消费者提供值得信赖的选择。
                    </p>
                  </div>

                  <div className="grid grid-cols-3 gap-4 mt-8">
                    <div className="text-center">
                      <div className="text-2xl md:text-3xl font-heading font-bold text-primary-600">5+</div>
                      <p className="text-xs text-foreground-500 mt-1">年行业深耕</p>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl md:text-3xl font-heading font-bold text-primary-600">500+</div>
                      <p className="text-xs text-foreground-500 mt-1">精选产品</p>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl md:text-3xl font-heading font-bold text-primary-600">50万+</div>
                      <p className="text-xs text-foreground-500 mt-1">服务用户</p>
                    </div>
                  </div>
                </div>
                <div className="aspect-[4/3] rounded-lg overflow-hidden bg-background-100">
                  <img
                    src="https://readdy.ai/api/search-image?query=Elegant%20luxury%20brand%20concept%20photography%2C%20minimalist%20cream%20and%20rose%20gold%20interior%20with%20premium%20product%20displays%2C%20soft%20warm%20lighting%2C%20sophisticated%20boutique%20atmosphere%2C%20editorial%20quality%2C%20clean%20composition&width=800&height=600&seq=about-brand-01&orientation=landscape"
                    alt="臻品阁品牌形象"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Quality Promise */}
          <section className="w-full py-14 md:py-20 px-6 md:px-10 bg-background-100/70">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-10 md:mb-14">
                <p className="text-xs md:text-sm font-label text-secondary-500 tracking-[0.2em] uppercase mb-2">
                  Quality Promise
                </p>
                <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900">
                  品质承诺
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                {[
                  {
                    icon: 'ri-shield-check-line',
                    title: '安全认证',
                    desc: '所有产品均通过国际权威机构安全检测认证，包括SGS、FDA、CE等。材质安全是我们的首要承诺。',
                  },
                  {
                    icon: 'ri-eye-off-line',
                    title: '隐私保障',
                    desc: '从下单到收货，全程私密保护。无标识外包装，快递面单无敏感信息，数据加密存储，守护您的隐私安全。',
                  },
                  {
                    icon: 'ri-medal-line',
                    title: '正品保证',
                    desc: '所有商品均为品牌正品，拥有完整的品牌授权链路。我们承诺假一赔十，让您购物无忧。',
                  },
                  {
                    icon: 'ri-truck-line',
                    title: '高效配送',
                    desc: '全国主要城市次日达，偏远地区3日内送达。支持顺丰、京东等主流快递，全程可追踪。',
                  },
                  {
                    icon: 'ri-refresh-line',
                    title: '无忧退换',
                    desc: '7天无理由退换（未拆封），30天质量问题免费换新。专属客服一对一处理，售后无忧。',
                  },
                  {
                    icon: 'ri-heart-line',
                    title: '用心服务',
                    desc: '专业顾问团队7×24小时在线，提供选购指导与使用建议。每一次沟通，我们都用心对待。',
                  },
                ].map((item) => (
                  <div
                    key={item.title}
                    className="p-5 md:p-6 rounded-lg bg-background-50 border border-background-200/50 hover:border-primary-200/40 transition-all duration-300"
                  >
                    <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-primary-100/70 mb-3 md:mb-4">
                      <i className={`${item.icon} text-lg md:text-xl text-primary-600`} />
                    </div>
                    <h3 className="text-sm md:text-base font-heading font-semibold text-foreground-900 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="w-full py-14 md:py-20 px-6 md:px-10 bg-background-50">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14">
                {/* Contact Info */}
                <div>
                  <p className="text-xs md:text-sm font-label text-secondary-500 tracking-[0.2em] uppercase mb-2">
                    Contact Us
                  </p>
                  <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-4">
                    联系我们
                  </h2>
                  <p className="text-sm text-foreground-600 mb-6 leading-relaxed">
                    如有任何疑问或建议，欢迎通过以下方式联系我们。我们的客服团队将竭诚为您服务。
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-100">
                        <i className="ri-mail-line text-accent-700" />
                      </div>
                      <div>
                        <p className="text-xs font-label font-medium text-foreground-900">电子邮箱</p>
                        <p className="text-sm text-foreground-600">service@zhenpinge.com</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-100">
                        <i className="ri-phone-line text-accent-700" />
                      </div>
                      <div>
                        <p className="text-xs font-label font-medium text-foreground-900">客服热线</p>
                        <p className="text-sm text-foreground-600">400-888-9999</p>
                        <p className="text-xs text-foreground-400">周一至周日 9:00 - 22:00</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-100">
                        <i className="ri-wechat-line text-accent-700" />
                      </div>
                      <div>
                        <p className="text-xs font-label font-medium text-foreground-900">微信客服</p>
                        <p className="text-sm text-foreground-600">臻品阁官方客服</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-100">
                        <i className="ri-map-pin-line text-accent-700" />
                      </div>
                      <div>
                        <p className="text-xs font-label font-medium text-foreground-900">公司地址</p>
                        <p className="text-sm text-foreground-600">上海市静安区南京西路1266号恒隆广场</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact Form */}
                <div className="bg-background-100/60 rounded-lg p-6 md:p-8 border border-background-200/50">
                  <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-4">
                    在线留言
                  </h3>
                  {formStatus === 'success' ? (
                    <div className="text-center py-10">
                      <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-100 mb-4">
                        <i className="ri-check-line text-2xl text-primary-600" />
                      </div>
                      <p className="text-foreground-900 font-medium">感谢您的留言！</p>
                      <p className="text-sm text-foreground-500 mt-1">我们将在24小时内与您联系</p>
                      <button
                        onClick={() => setFormStatus('idle')}
                        className="mt-4 px-4 py-2 text-sm rounded-full bg-background-200 hover:bg-background-300 transition-colors cursor-pointer"
                      >
                        继续留言
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} data-readdy-form="">
                      <div className="space-y-4">
                        <div>
                          <label htmlFor="contact-name" className="block text-xs font-label font-medium text-foreground-700 mb-1.5">
                            姓名 <span className="text-primary-500">*</span>
                          </label>
                          <input
                            id="contact-name"
                            name="name"
                            type="text"
                            required
                            className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                            placeholder="请输入您的姓名"
                          />
                        </div>
                        <div>
                          <label htmlFor="contact-email" className="block text-xs font-label font-medium text-foreground-700 mb-1.5">
                            邮箱 <span className="text-primary-500">*</span>
                          </label>
                          <input
                            id="contact-email"
                            name="email"
                            type="email"
                            required
                            className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                            placeholder="请输入您的邮箱"
                          />
                        </div>
                        <div>
                          <label htmlFor="contact-subject" className="block text-xs font-label font-medium text-foreground-700 mb-1.5">
                            主题
                          </label>
                          <input
                            id="contact-subject"
                            name="subject"
                            type="text"
                            className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                            placeholder="请输入留言主题"
                          />
                        </div>
                        <div>
                          <label htmlFor="contact-message" className="block text-xs font-label font-medium text-foreground-700 mb-1.5">
                            留言内容 <span className="text-primary-500">*</span>
                          </label>
                          <textarea
                            id="contact-message"
                            name="message"
                            required
                            maxLength={500}
                            rows={4}
                            className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                            placeholder="请输入您的留言内容（最多500字）"
                          />
                          <p className="text-[10px] text-foreground-400 mt-1 text-right">最多500字</p>
                        </div>

                        {/* Honeypot */}
                        <input
                          type="text"
                          name="phone_alt"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          readOnly
                          className="website-alt-field"
                        />

                        {formStatus === 'error' && (
                          <p className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg">{formError}</p>
                        )}

                        <button
                          type="submit"
                          className="w-full py-2.5 text-sm font-label font-medium whitespace-nowrap rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
                        >
                          提交留言
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* About FAQ */}
          <section className="w-full py-14 md:py-20 px-6 md:px-10 bg-background-100/70">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900">
                  常见问题
                </h2>
              </div>
              <div className="space-y-3">
                {faqs.slice(0, 4).map((faq, idx) => (
                  <div key={idx} className="rounded-lg bg-background-50 border border-background-200/50 overflow-hidden">
                    <button
                      onClick={() => setOpenFAQ(openFAQ === idx ? null : idx)}
                      className="w-full flex items-center justify-between p-4 text-left cursor-pointer hover:bg-background-100/50 transition-colors"
                    >
                      <span className="text-sm font-label font-medium text-foreground-900 pr-4">{faq.question}</span>
                      <div className={`w-5 h-5 flex-shrink-0 flex items-center justify-center rounded-full transition-all duration-300 ${openFAQ === idx ? 'bg-primary-500 text-background-50 rotate-45' : 'bg-background-200 text-foreground-500'}`}>
                        <i className="ri-add-line text-xs" />
                      </div>
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ${openFAQ === idx ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                      <p className="px-4 pb-4 text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}